import { OperationVariables } from '@apollo/client';
import { useApolloClient, useSubscription } from '@apollo/client/react';
import { getThrottled } from '@see-power-pft-common-js/utils/throttle-debounce';
import {
  GET_ANCILLARY_TRADES,
  GET_BALANCING_MECHANISM_TRADES,
  GET_SITE_TRADE_SUMMARY,
  GET_WHOLESALE_TRADES,
  SUBSCRIBE_TO_UPDATED_TRADE,
} from 'pages/Optimisation/queries';
import { useMemo } from 'react';
import { ApiName } from 'types/apiName';

export const useTradeDataSubscriber = (
  apiName: ApiName,
  siteId: number | undefined,
) => {
  const apolloClient = useApolloClient();

  const subscriptionOptions = useMemo(() => {
    const throttledDebouncedRefetchQueries = getThrottled(
      () => {
        apolloClient.refetchQueries({
          include: [
            GET_SITE_TRADE_SUMMARY,
            GET_WHOLESALE_TRADES,
            GET_ANCILLARY_TRADES,
            GET_BALANCING_MECHANISM_TRADES,
          ],
        });
      },
      { intervalMs: 1000, noLeading: true },
    );

    const options: useSubscription.Options<unknown, OperationVariables> = {
      context: { apiName },
      ignoreResults: true,
      onData: ({ data: { data } }) => {
        if (!siteId || !data) {
          return;
        }

        const updatedTradeSiteId = (
          data as { updatedTrade?: { siteId?: number; tradeSourceKey: string } }
        ).updatedTrade?.siteId;

        if (updatedTradeSiteId !== siteId) {
          return;
        }

        throttledDebouncedRefetchQueries();
      },
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('updatedTrade subscription error:', error);
      },
    };

    return options;
  }, [apolloClient, apiName, siteId]);

  useSubscription(SUBSCRIBE_TO_UPDATED_TRADE, subscriptionOptions);
};

export const usePlutoApiTradeDataSubscriber = (siteId: number | undefined) =>
  useTradeDataSubscriber(ApiName.plutoApi, siteId);
